/* eslint-disable react-refresh/only-export-components */
import { createWithEqualityFn as create } from 'zustand/traditional';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
}

// Track active timeout IDs for cleanup
const activeTimeouts = new Map<string, NodeJS.Timeout>();

interface ToastStore {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],
  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = { ...toast, id };
    set((state) => ({ toasts: [...state.toasts, newToast] }));

    // Auto-remove after duration - track timeout for cleanup
    if (toast.duration !== 0) {
      const timeoutId = setTimeout(() => {
        // Check if toast still exists before removing (prevents setState after unmount)
        const currentState = get();
        if (currentState.toasts.some((t) => t.id === id)) {
          set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
        }
        activeTimeouts.delete(id);
      }, toast.duration || 5000);

      activeTimeouts.set(id, timeoutId);
    }
  },
  removeToast: (id) => {
    // Clear the timeout if it exists
    const timeoutId = activeTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      activeTimeouts.delete(id);
    }
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
  clearToasts: () => {
    // Clear all active timeouts
    activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    activeTimeouts.clear();
    set({ toasts: [] });
  },
}));

export const useToast = () => {
  // Use shallow comparison for multiple selectors to prevent 4x re-renders
  const toastSelector = (state: ToastStore) => ({
    toasts: state.toasts,
    addToast: state.addToast,
    removeToast: state.removeToast,
    clearToasts: state.clearToasts,
  });
  const store = useToastStore(toastSelector, shallow);

  return {
    toasts: store.toasts,
    addToast: store.addToast,
    removeToast: store.removeToast,
    clearToasts: store.clearToasts,
    success: (title: string, message: string, duration?: number) =>
      store.addToast({ type: 'success', title, message, duration }),
    error: (title: string, message: string, duration?: number) =>
      store.addToast({ type: 'error', title, message, duration }),
    info: (title: string, message: string, duration?: number) =>
      store.addToast({ type: 'info', title, message, duration }),
    warning: (title: string, message: string, duration?: number) =>
      store.addToast({ type: 'warning', title, message, duration }),
  };
};

const toastIcons = {
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  error: <AlertCircle className="w-5 h-5 text-red-500" />,
  info: <Info className="w-5 h-5 text-blue-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
};

const toastStyles = {
  success: 'border-green-500 bg-green-50',
  error: 'border-red-500 bg-red-50',
  info: 'border-blue-500 bg-blue-50',
  warning: 'border-yellow-500 bg-yellow-50',
};

export function ToastContainer() {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg shadow-lg border-l-4 min-w-[300px] max-w-md',
            toastStyles[toast.type]
          )}
        >
          <div className="shrink-0 mt-0.5">{toastIcons[toast.type]}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900">{toast.title}</p>
            {toast.message && <p className="text-sm text-slate-600 mt-1">{toast.message}</p>}
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className="shrink-0 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// Global event listener for API client errors
// FIXED: Use store method directly to avoid re-registration on every render
export function useToastEventListeners() {
  // Get the store's addToast method directly (stable reference)
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    const handleToastError = (e: Event) => {
      const customEvent = e as CustomEvent<{ message: string }>;
      addToast({ type: 'error', title: 'Erro', message: customEvent.detail.message });
    };

    const handleToastWarning = (e: Event) => {
      const customEvent = e as CustomEvent<{ title?: string; message: string }>;
      addToast({
        type: 'warning',
        title: customEvent.detail.title || 'Aviso',
        message: customEvent.detail.message,
      });
    };

    window.addEventListener('toast:error', handleToastError);
    window.addEventListener('toast:warning', handleToastWarning);

    return () => {
      window.removeEventListener('toast:error', handleToastError);
      window.removeEventListener('toast:warning', handleToastWarning);
    };
  }, [addToast]); // addToast is now stable (from zustand selector)
}
